import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type * as LeafletNS from 'leaflet';
import { MemberLocation } from '../../../../core/services/member.service';

type LatLngTuple = [number, number];

@Component({
  selector: 'app-location-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './location-picker.component.html',
  styleUrls: ['./location-picker.component.css']
})
export class LocationPickerComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() value: MemberLocation | null = null;
  @Input() defaultCenter: LatLngTuple = [6.9271, 79.8612]; // Colombo, Sri Lanka
  @Input() defaultZoom = 12;

  @Output() valueChange = new EventEmitter<MemberLocation | null>();

  @ViewChild('mapEl', { static: true }) mapEl!: ElementRef<HTMLDivElement>;

  private L: typeof LeafletNS | null = null;
  private map: LeafletNS.Map | null = null;
  private marker: LeafletNS.Marker | null = null;
  private locating = false;
  private viewInitialised = false;

  get currentLat(): number | null {
    return this.value?.latitude ?? null;
  }

  get currentLng(): number | null {
    return this.value?.longitude ?? null;
  }

  get isLocating(): boolean {
    return this.locating;
  }

  async ngAfterViewInit(): Promise<void> {
    if (typeof window === 'undefined') return;
    this.L = await import('leaflet');
    this.fixDefaultIcon(this.L);

    const start: LatLngTuple =
      this.value?.latitude != null && this.value?.longitude != null
        ? [this.value.latitude, this.value.longitude]
        : this.defaultCenter;

    const startZoom = this.value?.latitude != null ? 15 : this.defaultZoom;

    this.map = this.L.map(this.mapEl.nativeElement).setView(start, startZoom);

    this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    if (this.value?.latitude != null && this.value?.longitude != null) {
      this.placeMarker(start, false);
    }

    this.map.on('click', (e: LeafletNS.LeafletMouseEvent) => {
      this.placeMarker([e.latlng.lat, e.latlng.lng], true);
    });

    this.viewInitialised = true;

    // Map sometimes initialises with wrong size when inside a modal; refresh
    setTimeout(() => this.map?.invalidateSize(), 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewInitialised || !this.L || !this.map) return;
    if (changes['value']) {
      const v = changes['value'].currentValue as MemberLocation | null;
      if (v?.latitude != null && v?.longitude != null) {
        this.placeMarker([v.latitude, v.longitude], false);
        this.map.setView([v.latitude, v.longitude], 15);
      } else if (this.marker) {
        this.marker.remove();
        this.marker = null;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.marker) {
      this.marker.remove();
      this.marker = null;
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  useCurrentLocation(): void {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    this.locating = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.locating = false;
        const coords: LatLngTuple = [pos.coords.latitude, pos.coords.longitude];
        this.map?.setView(coords, 16);
        this.placeMarker(coords, true);
      },
      () => { this.locating = false; },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  clear(): void {
    if (this.marker) {
      this.marker.remove();
      this.marker = null;
    }
    this.valueChange.emit(null);
  }

  private placeMarker(coords: LatLngTuple, emit: boolean): void {
    if (!this.L || !this.map) return;

    if (this.marker) {
      this.marker.setLatLng(coords);
    } else {
      this.marker = this.L.marker(coords, { draggable: true }).addTo(this.map);
      this.marker.on('dragend', () => {
        const ll = this.marker!.getLatLng();
        this.emitValue(ll.lat, ll.lng);
      });
    }

    if (emit) {
      this.emitValue(coords[0], coords[1]);
    }
  }

  private emitValue(lat: number, lng: number): void {
    this.valueChange.emit({
      latitude: +lat.toFixed(6),
      longitude: +lng.toFixed(6),
      formattedAddress: this.value?.formattedAddress ?? null
    });
  }

  /**
   * Leaflet bundles its marker icon as a relative URL that breaks under Angular's bundler.
   * Reset the default icon to point at the bundled images directly.
   */
  private fixDefaultIcon(L: typeof LeafletNS): void {
    const iconRetinaUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png';
    const iconUrl       = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png';
    const shadowUrl     = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png';

    const proto = (L.Icon.Default.prototype as any);
    delete proto._getIconUrl;
    L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });
  }
}
